/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { Fragment, useState } from 'react';
import type { FormulaDto } from '@shared/types';
import { useFormulaListStore } from '../../stores/formulaListStore';
import { Tooltip } from '../layout/Tooltip';

interface FormulaTableProps {
  formulas: FormulaDto[];
  showCheckboxes?: boolean;
  onFormulaClick?: (setId: string) => void;
}

export function FormulaTable({ formulas, showCheckboxes = false, onFormulaClick }: FormulaTableProps) {
  const {
    selectedSetIds, toggleSelect, selectAllPage, deselectPage, selectAllMatchingResults,
    isIngredientSearch, searchResultCount, searchText, searchMode,
    status, sortBy, setSortBy,
  } = useFormulaListStore();
  const [showExpandPrompt, setShowExpandPrompt] = useState(false);

  const totalCols = (showCheckboxes ? 1 : 0) + 7; // checkbox + 6 data cols

  /** Highlight matching substring within ingredient name */
  function highlightMatch(text: string) {
    if (!searchText) return text;
    const idx = text.toLowerCase().indexOf(searchText.toLowerCase());
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + searchText.length);
    const after = text.slice(idx + searchText.length);
    return <>{before}<mark className="bg-yellow-200 text-purple-900 rounded-sm px-0.5">{match}</mark>{after}</>;
  }

  const allPageSelected = formulas.length > 0 && formulas.every((f) => selectedSetIds.has(f.setId));
  const somePageSelected = formulas.some((f) => selectedSetIds.has(f.setId));

  function handleSelectAllToggle() {
    if (allPageSelected) {
      deselectPage();
      setShowExpandPrompt(false);
    } else {
      selectAllPage();
      if (searchResultCount > formulas.length) {
        setShowExpandPrompt(true);
      }
    }
  }

  /** Source header cycles A→Z → Z→A → default order */
  function handleSourceSortToggle() {
    setSortBy(sortBy === 'source_asc' ? 'source_desc' : sortBy === 'source_desc' ? 'default' : 'source_asc');
  }

  async function handleExpandToAll() {
    await selectAllMatchingResults();
    setShowExpandPrompt(false);
  }

  const expandPromptLabel =
    searchMode === 'ingredient' ? `Select all ${searchResultCount} formulas matching this ingredient search?`
    : searchMode === 'formula' ? `Select all ${searchResultCount} formulas matching this name search?`
    : !!searchText ? `Select all ${searchResultCount} formulas matching this search?`
    : `Select all ${searchResultCount} matching formulas?`;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200 text-left text-gray-600">
            {showCheckboxes && (
              <th className="px-3 py-2 w-10">
                <div className="flex items-center gap-1.5">
                  <input
                    type="checkbox"
                    aria-label="Select all formulas on this page"
                    checked={allPageSelected}
                    ref={(el) => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                    onChange={handleSelectAllToggle}
                    className="rounded border-gray-300"
                    disabled={formulas.length === 0}
                  />
                  {selectedSetIds.size > 0 && (
                    <span className="text-xs font-medium text-blue-600 whitespace-nowrap">{selectedSetIds.size}</span>
                  )}
                </div>
              </th>
            )}
            <th className="px-3 py-2 font-medium"><Tooltip text="Compounding formula name" position="bottom"><span className="cursor-help">Name</span></Tooltip></th>
            <th className="px-3 py-2 font-medium"><Tooltip text="Physical form (cream, capsule, solution, etc.)" position="bottom"><span className="cursor-help">Dosage Form</span></Tooltip></th>
            <th className="px-3 py-2 font-medium"><Tooltip text="Medication concentration or amount" position="bottom"><span className="cursor-help">Strength</span></Tooltip></th>
            <th className="px-3 py-2 font-medium"><Tooltip text="DEA controlled substance schedule" position="bottom"><span className="cursor-help">Schedule</span></Tooltip></th>
            {status === 'assigned' ? (
              <th className="px-3 py-2 font-medium"><Tooltip text="Most recent taxonomy assignment date" position="bottom"><span className="cursor-help">Assigned Date</span></Tooltip></th>
            ) : (
              <th
                className="px-3 py-2 font-medium"
                aria-sort={sortBy === 'source_asc' ? 'ascending' : sortBy === 'source_desc' ? 'descending' : 'none'}
              >
                <Tooltip text="Formulary source system — click to sort" position="bottom">
                  <button type="button" onClick={handleSourceSortToggle} className="inline-flex items-center gap-1 font-medium hover:text-gray-900">
                    Source
                    {sortBy === 'source_asc' && <span aria-hidden="true">▲</span>}
                    {sortBy === 'source_desc' && <span aria-hidden="true">▼</span>}
                  </button>
                </Tooltip>
              </th>
            )}
            <th className="px-3 py-2 font-medium w-24 text-center"><Tooltip text="Whether taxonomy codes have been assigned" position="bottom"><span className="cursor-help">Status</span></Tooltip></th>
          </tr>
        </thead>
        {showExpandPrompt && (
          <tbody>
            <tr className="bg-blue-50 border-b border-blue-200">
              <td colSpan={totalCols} className="px-3 py-2 text-sm text-blue-800">
                All {formulas.length} formulas on this page are selected.{' '}
                <button
                  onClick={handleExpandToAll}
                  className="font-medium underline hover:text-blue-900"
                >
                  {expandPromptLabel}
                </button>
                <button
                  onClick={() => setShowExpandPrompt(false)}
                  className="ml-3 text-blue-500 hover:text-blue-700"
                >
                  Dismiss
                </button>
              </td>
            </tr>
          </tbody>
        )}
        <tbody>
          {formulas.map((f) => {
            const hasIngredients = f.ingredients && f.ingredients.length > 0;

            return (
              <Fragment key={f.setId}>
                <tr
                  data-testid="formula-row"
                  onClick={() => onFormulaClick?.(f.setId)}
                  className="border-b border-gray-100 hover:bg-blue-50 cursor-pointer transition-colors"
                >
                  {showCheckboxes && (
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select ${f.name}`}
                        checked={selectedSetIds.has(f.setId)}
                        onChange={() => toggleSelect(f.setId)}
                        className="rounded border-gray-300"
                      />
                    </td>
                  )}
                  <td className="px-3 py-2 font-medium text-gray-900">{f.name}</td>
                  <td className="px-3 py-2 text-gray-600">{f.dosageForm || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{f.strength || '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{f.schedule || '—'}</td>
                  {status === 'assigned' ? (
                    <td className="px-3 py-2 text-gray-600">
                      {f.lastAssignedDate ? new Date(f.lastAssignedDate).toLocaleDateString() : '—'}
                    </td>
                  ) : (
                    <td className="px-3 py-2 text-gray-600">{f.formulaSourceName || '—'}</td>
                  )}
                  <td className="px-3 py-2 text-center">
                    {f.isAssigned ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        Assigned
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                        Unassigned
                      </span>
                    )}
                  </td>
                </tr>

                {isIngredientSearch && hasIngredients && (
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <td colSpan={totalCols} className="px-6 py-2">
                      <div className="flex flex-wrap gap-2 items-center">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide mr-1">Ingredients:</span>
                        {f.ingredients.map((ing, idx) => (
                          <span
                            key={ing.setId || idx}
                            className="inline-block px-2 py-0.5 rounded bg-purple-50 text-purple-700 text-xs font-mono"
                          >
                            {highlightMatch(ing.name)}
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
          {formulas.length === 0 && (
            <tr>
              <td colSpan={totalCols} className="px-3 py-8 text-center text-gray-400">
                No formulas found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
